import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../utils/firebaseConfig';
import TableViewer from './TableViewer';
import '../styles/Locations.css';

const META_KEYS = new Set([
    'id', 'location', 'location_name', 'locationName', 'store', 'store_name', 'storeName',
    'date', 'timestamp', 'createdAt', 'updatedAt', 'period', 'periodLabel', 'period_type',
    'periodType', 'quarter', 'year', 'isQTD', 'qtd', 'period_end', 'periodEnd', 'week_end', 'weekEnd',
]);
const DATE_KEYS = ['date', 'timestamp', 'period_end', 'periodEnd', 'week_end', 'weekEnd', 'updatedAt', 'createdAt'];
const LOCATION_KEYS = ['location', 'location_name', 'locationName', 'store', 'store_name', 'storeName'];

const asDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};
const recordDate = (record) => DATE_KEYS.map((key) => asDate(record[key])).find(Boolean) || null;
const locationName = (record) => LOCATION_KEYS.map((key) => record[key]).find((value) => typeof value === 'string' && value.trim())?.trim() || 'Unknown Location';
const quarterInfo = (date) => ({ year: date.getFullYear(), quarter: Math.floor(date.getMonth() / 3) + 1 });
const previousQuarter = ({ year, quarter }) => quarter === 1 ? { year: year - 1, quarter: 4 } : { year, quarter: quarter - 1 };
const sameQuarter = (left, right) => left.year === right.year && left.quarter === right.quarter;
const parseQuarter = (value) => {
    const match = String(value || '').match(/Q([1-4])\D*(20\d{2})/i) || String(value || '').match(/(20\d{2})\D*Q([1-4])/i);
    if (!match) return null;
    return match[0].toUpperCase().startsWith('Q')
        ? { quarter: Number(match[1]), year: Number(match[2]) }
        : { year: Number(match[1]), quarter: Number(match[2]) };
};
const periodText = (record) => [record.period, record.periodLabel, record.period_type, record.periodType, record.quarter].filter(Boolean).join(' ');
const bucketFor = (record, current, previous) => {
    const text = periodText(record);
    if (record.isQTD === true || record.qtd === true || /\bqtd\b|current quarter/i.test(text)) return 'qtd';
    if (/last quarter|previous quarter|prior quarter/i.test(text)) return 'previous';
    const explicitQuarter = parseQuarter(text);
    if (explicitQuarter && sameQuarter(explicitQuarter, current)) return 'qtd';
    if (explicitQuarter && sameQuarter(explicitQuarter, previous)) return 'previous';
    const date = recordDate(record);
    if (!date) return null;
    const quarter = quarterInfo(date);
    if (sameQuarter(quarter, current)) return 'qtd';
    if (sameQuarter(quarter, previous)) return 'previous';
    return null;
};
const normalizedKey = (key) => key.replace(/[^a-z0-9]/gi, '').toLowerCase();
const labelFor = (key) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const typeFor = (key) => {
    const normalized = normalizedKey(key);
    if (/percent|pct|margin|rate|ratio/.test(normalized)) return 'float';
    if (/gp|grossprofit|revenue|commission|bonus|salesamount|cogs|discount|profit/.test(normalized)) return 'currency';
    if (/count|sales|repairs|quotes|units/.test(normalized) && !/per|percent|rate/.test(normalized)) return 'int';
    return 'float';
};
const latestRecord = (records) => [...records].sort((a, b) => (recordDate(b)?.getTime() || 0) - (recordDate(a)?.getTime() || 0))[0];
const gpKeyFrom = (keys) => {
    const preferred = ['totalgp', 'gp', 'grossprofit', 'grossprofitdollars'];
    return preferred.map((name) => keys.find((key) => normalizedKey(key) === name)).find(Boolean)
        || keys.find((key) => /gp|grossprofit/.test(normalizedKey(key)))
        || null;
};
const formatValue = (value, key) => {
    if (!Number.isFinite(Number(value))) return '—';
    const number = Number(value);
    return typeFor(key) === 'currency' ? `$${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const Locations = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => onSnapshot(collection(firestore, 'LocationsKPI'), (snapshot) => {
        setRecords(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
        setError('');
    }, (snapshotError) => {
        console.error('Error loading LocationsKPI:', snapshotError);
        setError('Unable to load location performance data.');
        setLoading(false);
    }), []);

    const model = useMemo(() => {
        const datedRecords = records.map((record) => recordDate(record)).filter(Boolean);
        const anchor = datedRecords.sort((a, b) => b - a)[0] || new Date();
        const current = quarterInfo(anchor);
        const previous = previousQuarter(current);
        const numericKeys = [...new Set(records.flatMap((record) => Object.keys(record).filter((key) => {
            const value = record[key];
            return !META_KEYS.has(key) && value !== null && value !== '' && typeof value !== 'boolean' && Number.isFinite(Number(value));
        })))];
        const gpKey = gpKeyFrom(numericKeys);
        const grouped = records.reduce((result, record) => {
            const location = locationName(record);
            const bucket = bucketFor(record, current, previous);
            if (!bucket) return result;
            if (!result[location]) result[location] = { qtd: [], previous: [] };
            result[location][bucket].push(record);
            return result;
        }, {});
        const locations = Object.keys(grouped).sort().slice(0, 3);
        const rows = locations.flatMap((location) => {
            const previousRecord = latestRecord(grouped[location].previous);
            const currentRecord = latestRecord(grouped[location].qtd);
            return [
                { location, period: `Q${previous.quarter} ${previous.year}`, ...Object.fromEntries(numericKeys.map((key) => [key, previousRecord?.[key]])) },
                { location, period: `Q${current.quarter} ${current.year} QTD`, ...Object.fromEntries(numericKeys.map((key) => [key, currentRecord?.[key]])) },
            ];
        });
        const cards = locations.map((location) => {
            const record = latestRecord(grouped[location].qtd) || latestRecord(grouped[location].previous);
            return { location, record, gp: gpKey ? Number(record?.[gpKey]) || 0 : 0 };
        }).sort((a, b) => b.gp - a.gp).slice(0, 3);
        const columns = {
            location: { name: 'Location', pos: 1 },
            period: { name: 'Period', pos: 2 },
            ...Object.fromEntries(numericKeys.map((key, index) => [key, { name: labelFor(key), type: typeFor(key), pos: index + 3, just: 'right' }])),
        };
        return { cards, columns, current, gpKey, numericKeys, rows };
    }, [records]);

    if (loading) return <div className="locations-status">Loading location data…</div>;
    if (error) return <div className="locations-status error" role="alert">{error}</div>;
    if (!records.length) return (
        <div className="locations locations-empty">
            <p className="eyebrow">Prepared module</p><h1>Location Performance</h1>
            <div className="empty-state"><strong>LocationsKPI is ready for data.</strong><span>The three cards and six-row quarterly table will populate automatically when the collection begins receiving records.</span></div>
        </div>
    );

    const cardMetrics = [model.gpKey, ...model.numericKeys.filter((key) => key !== model.gpKey)].filter(Boolean).slice(0, 4);
    return (
        <div className="locations">
            <header className="locations-header"><div><p className="eyebrow">Quarterly comparison</p><h1>Location Performance</h1><p>Latest available QTD snapshot compared with the previous quarter.</p></div><span className="live-badge">Live from LocationsKPI</span></header>
            {!model.rows.length ? <div className="empty-state">Data was received, but no current or previous-quarter records could be identified. Include a date, timestamp, quarter, or period label in each document.</div> : (
                <>
                    <section className="location-cards" aria-label="Location summary cards">
                        {model.cards.map((card, index) => (
                            <article className={`location-card ${model.gpKey && index === 0 ? 'leader' : ''}`} key={card.location}>
                                <div className="location-card-heading"><div><small>{model.gpKey && index === 0 ? 'GP leader' : `Q${model.current.quarter} QTD`}</small><h2>{card.location}</h2></div>{model.gpKey && index === 0 && <span>Top GP</span>}</div>
                                <div className="location-card-metrics">
                                    {cardMetrics.map((key) => <p key={key}><strong>{labelFor(key)}</strong><span>{formatValue(card.record?.[key], key)}</span></p>)}
                                </div>
                            </article>
                        ))}
                    </section>
                    <section className="locations-table-section"><div><h2>Quarterly detail</h2><p>Highest numeric values in each visible column are bolded.</p></div><TableViewer dataKey={model.rows} colFormatDict={model.columns} defaultSortBy={model.gpKey} /></section>
                </>
            )}
        </div>
    );
};

export default Locations;
